import _ from 'lodash'
import {
    CreateExtArgs,
    CreateExtensionArgument,
    DalValue,
    DmApis,
    DmStore,
    DocumentDataTypes,
    Extension,
    ExtensionAPI,
    type ExtensionPublicAPI,
    type InitializeExtArgs,
    ModelsInitializationType,
    PointerMethods,
    pointerUtils,
    ValidatorMap
} from '@wix/document-manager-core'
import type {
    AppDefinitionId,
    ClientSpecMap,
    ClientSpecMapEntry,
    CSMRefreshCallback,
    EditorClientSpecMapEntry,
    Pointer
} from '@wix/document-services-types'
import {isBuilderComponent, ReportableError, ReportableWarning, TPA_CONSTANTS} from '@wix/document-manager-utils'
import type {PageExtensionAPI} from './page'
import {DATA_TYPES, RESOURCE_TYPES} from '../constants/constants'
import {
    hasEditorPlatformPart,
    isAppControllerData,
    isAppExcludedFromAutoRevoke,
    isAppPlatformEditorOnly,
    isTpaByDataType,
    isUnifiedComponentData
} from './tpa/installedTpaAppsUtils'
import type {RMApi} from './rendererModel'
import {deepClone} from '@wix/wix-immutable-proxy'
import {fetchWithAuth} from '../utils/fetchUtils'
import {extractBuilderComponentInfo} from '../utils/appUtils'
import type {ComponentsMetadataAPI, ComponentsMetadataExtension} from './componentsMetadata/componentsMetadata'

const {getPointer} = pointerUtils
const pointerType = 'platform'
const pagesPlatformPointerType = 'pagesPlatformApplications'
const semanticAppVersionsPointerType = 'semanticAppVersions'
const appsStatePointerType = 'appsState'

export interface BuilderCompAppData {
    appDefinitionId: string
    componentId: string
    refType?: string
}

export interface PlatformExtensionAPI extends ExtensionAPI {
    platform: {
        getAllAppsInstalledOnPages(): Set<AppDefinitionId>
        pagesPlatformApplication: {
            updatePagePlatformApp(pageRef: Pointer, appDefinitionId: AppDefinitionId, value: any): void
        }
        getAppDefinitionIdFromApplicationId(applicationId: string, info: Record<string, any>): AppDefinitionId
        getAppInstance(appId: string, forceRefresh: boolean): Promise<string>
        shouldExcludeFromAutoRevoke(appData: any): boolean
        getBuilderCompAppData(componentType: string): BuilderCompAppData | undefined
        addBuilderCompAppData(appDefinitionId: string): void
        loadBuilderResourcesForApps(appDefinitionIds: string[]): Promise<void>
        getBuilderCompReferredType(componentType: string): string | undefined
        conductPlatformExperiments(): Promise<Record<string, any>>
        getBuilderCompReferringTypes(componentType: string): string[]
        registerClientSpecMapRefreshCallback(callback: CSMRefreshCallback): void
        unregisterClientSpecMapRefreshCallback(callback: CSMRefreshCallback): void
        getClientSpecMapRefreshCallbacks(): CSMRefreshCallback[]
        getBuilderCompTypesToReferredTypesMap(): Record<string, string>
        getBuilderCompAppDataMap(): Record<string, BuilderCompAppData>
    }
}

const getDocumentDataTypes = (): DocumentDataTypes => ({
    [pointerType]: {},
    [pagesPlatformPointerType]: {},
    [appsStatePointerType]: {}
})

const initialState = {
    [appsStatePointerType]: {
        [appsStatePointerType]: {}
    }
}

const generateBuilderCompAppData = (csmEntry: ClientSpecMapEntry) => {
    const builderTypeToAppData: Record<string, any> = {}
    const appDefinitionId = _.get(csmEntry, ['appDefinitionId'])

    if (appDefinitionId) {
        const components = (csmEntry as EditorClientSpecMapEntry).components ?? []

        _.forEach(components, (component: any) => {
            const {componentId} = component
            const {compType, refType} = extractBuilderComponentInfo(component)

            if (compType) {
                builderTypeToAppData[compType] = {
                    componentId,
                    appDefinitionId,
                    refType
                }
            }
        })
    }

    return builderTypeToAppData
}
const generateBuilderCompAppDataMap = (clientSpecMap: ClientSpecMap) => {
    return _.reduce(
        clientSpecMap,
        (res, csmEntry: ClientSpecMapEntry) => {
            const builderTypeToAppData = generateBuilderCompAppData(csmEntry)
            return {...res, ...builderTypeToAppData}
        },
        {}
    )
}

const initializeModels = (initialStore: DmStore, initialModels: ModelsInitializationType, {pointers}: DmApis) => {
    initialStore.set(
        pointers.platform.getPagesPlatformApplicationsPointer(),
        initialModels.rendererModel.pagesPlatformApplications
    )
}

const registerBuilderCompsMetadata = (
    builderCompsData: Record<string, any>,
    componentsMetadata: ComponentsMetadataExtension
) => {
    _.forEach(builderCompsData, (builderCompData: any, componentType: string) => {
        const {refType} = builderCompData
        if (refType) {
            componentsMetadata.registrar.deriveComponentMetadata(componentType, refType)
        }
    })
}

const createExtension = ({experimentInstance, logger, environmentContext}: CreateExtensionArgument): Extension => {
    let builderCompAppDataMap: Record<string, BuilderCompAppData> = {}

    const createValidator = ({dal, pointers}: DmApis): ValidatorMap => {
        const appsRelatedDataTypes = [
            ..._.values(TPA_CONSTANTS.DATA_TYPE),
            'Page',
            'CustomMenu',
            'Builder.MenuItems',
            'PagesGroupData',
            'AppController',
            'CustomElement'
        ]

        const validateAppsOnComps = (pointer: Pointer, value: DalValue) => {
            const {type, appDefinitionId, appDefId, applicationId, appId, tpaApplicationId} = value ?? {}
            const applicationIdOnData = applicationId ?? appId ?? tpaApplicationId
            const appDefIdOnData = appDefId ?? appDefinitionId

            if (pointer.type !== 'data' || !_.includes(appsRelatedDataTypes, type) || !applicationIdOnData) {
                return
            }

            const missingAppDefinitionId = isAppControllerData(value)
                ? _.isNil(applicationIdOnData)
                : _.isNil(appDefIdOnData)

            const appData =
                dal.get(pointers.general.getClientSpecMapEntry(applicationIdOnData)) ??
                dal.get(pointers.general.getClientSpecMapEntryByAppDefId(appDefIdOnData))

            if (appData && missingAppDefinitionId) {
                return [
                    {
                        shouldFail: experimentInstance.isOpen('dm_missingAppDefIdOnDataError'),
                        type: 'missingAppDefIdOnDataError',
                        message: 'missing appDefinitionId on data item',
                        extras: {
                            type,
                            pointer,
                            applicationId: applicationIdOnData,
                            appDefinitionId: appDefIdOnData
                        }
                    }
                ]
            }
        }

        return {
            validateAppsOnComps
        }
    }

    const createExtensionAPI = ({
        pointers,
        dal,
        extensionAPI,
        coreConfig,
        resourceLoader
    }: CreateExtArgs): PlatformExtensionAPI => {
        const updatePagePlatformApp = (pageRef: Pointer, appDefinitionId: AppDefinitionId, value: any) => {
            const pageId = pageRef.id
            const pagesPlatformApplicationPointer =
                pointers.platform.getPagesPlatformApplicationPointer(appDefinitionId)
            const applicationPages = deepClone(dal.get(pagesPlatformApplicationPointer)) || {}

            delete applicationPages[pageId]
            if (value) {
                _.set(applicationPages, pageId, true)
            }
            if (_.isEmpty(applicationPages)) {
                if (dal.has(pagesPlatformApplicationPointer)) {
                    dal.remove(pagesPlatformApplicationPointer)
                }
                return
            }
            dal.set(pagesPlatformApplicationPointer, applicationPages)
        }

        const handleMigrationError = (extras: Record<string, any>, tags: Record<string, any>) => {
            coreConfig.logger.captureError(
                new ReportableWarning({
                    errorType: 'appIdMigrationToAppDefIdError',
                    message: 'error on migration process from appId to appDefId',
                    extras,
                    tags
                })
            )
        }

        const getAppDefinitionIdFromApplicationId = (applicationId: string, info: Record<string, any>) => {
            try {
                const csmEntry = dal.get(pointers.general.getClientSpecMapEntry(applicationId.toString()))
                return csmEntry.appDefinitionId
            } catch (e) {
                handleMigrationError({applicationId, info}, {appDefIdToAppId: true})
            }
        }

        const shouldExcludeFromAutoRevoke = (appData: any) => {
            return isAppPlatformEditorOnly(appData) || isAppExcludedFromAutoRevoke(appData)
        }
        const getBuilderCompAppData = (componentType: string) => {
            return deepClone(builderCompAppDataMap[componentType])
        }

        const addBuilderCompAppData = (appDefinitionId: string) => {
            const csmEntry = dal.get(pointers.rendererModel.getClientSpecMapEntryByAppDefId(appDefinitionId))
            const builderData = generateBuilderCompAppData(csmEntry)
            builderCompAppDataMap = {...builderCompAppDataMap, ...builderData}
            const csmComponents = (csmEntry as EditorClientSpecMapEntry)?.components ?? []
            const ercComponentsCount = csmComponents.filter(isBuilderComponent).length
            coreConfig.logger.interactionStarted('builderCompAppData updated', {
                extras: {
                    appDefinitionId,
                    componentTypes: _.keys(builderData),
                    totalCsmComponents: csmComponents.length,
                    ercComponentsCount
                }
            })
            registerBuilderCompsMetadata(builderData, (extensionAPI as ComponentsMetadataAPI).componentsMetadata)
        }

        const loadBuilderResourcesForApps = async (appDefinitionIds: string[]) => {
            if (!appDefinitionIds.length) {
                return
            }
            try {
                await Promise.all([
                    resourceLoader.load(RESOURCE_TYPES.SCHEMA, appDefinitionIds, true),
                    resourceLoader.load(RESOURCE_TYPES.COMPONENTS, appDefinitionIds, true)
                ])
            } catch (error) {
                coreConfig.logger.captureError(
                    new ReportableError({
                        errorType: 'BUILDER_RESOURCES_LOADING_FAILURE',
                        message: 'Failed to load builder resources for apps',
                        extras: {
                            appDefinitionIds,
                            error: error instanceof Error ? error.message : String(error)
                        }
                    })
                )
                throw error
            }
            _.forEach(appDefinitionIds, addBuilderCompAppData)
        }

        const getBuilderCompReferredType = (componentType: string) => {
            const builderCompAppData = getBuilderCompAppData(componentType)
            return builderCompAppData?.refType
        }

        const conductPlatformExperiments = async (): Promise<Record<string, any>> => {
            try {
                const {siteAPI} = extensionAPI as RMApi
                const instance = siteAPI.getInstance()
                const url =
                    '/_api/wix-laboratory-server/v1/laboratory/platform/conductAllInScopesGrouped?scopes=viewer-apps-dataBinding'

                const result = await fetchWithAuth<any>(url, instance, environmentContext?.fetchFn, 'GET')
                const experiments = result?.values?.['viewer-apps-dataBinding']?.values || {}

                const currentPlatformAppsExperiments = dal.get({id: 'platformAppsExperiments', type: 'rendererModel'})
                dal.set(
                    {id: 'platformAppsExperiments', type: 'rendererModel'},
                    {...currentPlatformAppsExperiments, dataBinding: experiments}
                )
                return experiments
            } catch (error: any) {
                console.warn('Failed to conduct platform experiments:', error)
                logger?.captureError?.(error, {
                    tags: {
                        platformExperimentsFailed: true,
                        error: error?.message || String(error),
                        scopes: ['viewer-apps-dataBinding']
                    }
                })
                return {}
            }
        }

        const getBuilderCompReferringTypes = (componentType: string) => {
            return _.keys(_.pickBy(deepClone(builderCompAppDataMap), value => value.refType === componentType))
        }
        const getBuilderCompTypesToReferredTypesMap = (): Record<string, string> => {
            return _.mapValues(_.pickBy(deepClone(builderCompAppDataMap), 'refType'), 'refType') as Record<
                string,
                string
            >
        }

        const getBuilderCompAppDataMap = () => deepClone(builderCompAppDataMap)

        const onClientSpecMapRefresh: CSMRefreshCallback[] = []

        const registerClientSpecMapRefreshCallback = (callback: CSMRefreshCallback) => {
            onClientSpecMapRefresh.push(callback)
        }

        const unregisterClientSpecMapRefreshCallback = (callback: CSMRefreshCallback) => {
            const index = onClientSpecMapRefresh.indexOf(callback)
            if (index > -1) {
                onClientSpecMapRefresh.splice(index, 1)
            }
        }

        const getClientSpecMapRefreshCallbacks = (): CSMRefreshCallback[] => [...onClientSpecMapRefresh]

        return {
            platform: {
                getAllAppsInstalledOnPages: () => {
                    const {page} = extensionAPI as PageExtensionAPI
                    const {rendererModel} = extensionAPI as RMApi
                    const clientSpecMap = rendererModel.getClientSpecMap()
                    const dataItems = _.values(dal.query(DATA_TYPES.data, page.getAllPagesIndexId()))
                    return dataItems.reduce((allAppDefIds, dataItem) => {
                        const appDefIdOnData = isAppControllerData(dataItem)
                            ? dataItem?.applicationId
                            : dataItem?.appDefinitionId ?? dataItem?.appDefId

                        if (!appDefIdOnData) {
                            return allAppDefIds
                        }

                        const isTpaApp = isTpaByDataType(dataItem.type)
                        const isUnifiedComponent = isUnifiedComponentData(dataItem.type)

                        if (!isTpaApp && !isUnifiedComponent) {
                            const appData = _.find(
                                clientSpecMap,
                                app => _.get(app, ['appDefinitionId']) === appDefIdOnData
                            )
                            const isActive = !_.isNil(appData) && hasEditorPlatformPart(appData)
                            if (!isActive) {
                                return allAppDefIds
                            }
                        }
                        allAppDefIds.add(appDefIdOnData)
                        return allAppDefIds
                    }, new Set<string>())
                },
                pagesPlatformApplication: {
                    updatePagePlatformApp
                },
                getAppDefinitionIdFromApplicationId,
                getAppInstance: async (appId: string, forceRefresh: boolean = false): Promise<string> => {
                    const {rendererModel} = extensionAPI as RMApi
                    if (forceRefresh || rendererModel.isAppInstanceExpired(appId)) {
                        return await rendererModel.refreshAppInstance(appId)
                    }
                    return (rendererModel.getClientSpecMap()[appId] as ClientSpecMapEntry & {instance: string}).instance
                },
                shouldExcludeFromAutoRevoke,
                getBuilderCompAppData,
                addBuilderCompAppData,
                loadBuilderResourcesForApps,
                getBuilderCompReferredType,
                conductPlatformExperiments,
                getBuilderCompReferringTypes,
                getBuilderCompTypesToReferredTypesMap,
                getBuilderCompAppDataMap,
                registerClientSpecMapRefreshCallback,
                unregisterClientSpecMapRefreshCallback,
                getClientSpecMapRefreshCallbacks
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis): ExtensionPublicAPI => {
        const {platform} = extensionAPI as PlatformExtensionAPI
        return {
            platform: {
                getAppInstance: platform.getAppInstance,
                registerClientSpecMapRefreshCallback: platform.registerClientSpecMapRefreshCallback,
                unregisterClientSpecMapRefreshCallback: platform.unregisterClientSpecMapRefreshCallback
            }
        }
    }

    const initializeAfterDataLoaded = async (extArgs: InitializeExtArgs) => {
        const {extensionAPI, coreConfig} = extArgs
        const {rendererModel} = extensionAPI as RMApi

        const clientSpecMap = rendererModel.getClientSpecMap()
        builderCompAppDataMap = generateBuilderCompAppDataMap(clientSpecMap)
        coreConfig.logger.interactionStarted('builderCompAppDataMap-built')
        registerBuilderCompsMetadata(builderCompAppDataMap, (extensionAPI as ComponentsMetadataAPI).componentsMetadata)
    }

    const initialize = async (extArgs: InitializeExtArgs) => {
        const {eventEmitter, EVENTS, extensionAPI} = extArgs
        const {rendererModel} = extensionAPI as RMApi
        eventEmitter.on(EVENTS.APPS_INSTALL_STATE.UPDATE, appDefId => {
            rendererModel.askRemoteEditorsToRefreshClientSpecMap(appDefId)
        })
    }

    const createPointersMethods = (): PointerMethods => {
        const getPlatformPointer = () => getPointer('platform', pointerType)
        const getAppManifestPointer = (appDefinitionId: string) =>
            getPointer('appManifest', pointerType, {innerPath: [appDefinitionId]})
        const appPublicApiNamePointer = (appDefinitionId: string) =>
            getPointer('platform', pointerType, {innerPath: ['appPublicApiName', appDefinitionId]})
        const appPrivateApiNamePointer = (appDefinitionId: string) =>
            getPointer('platform', pointerType, {innerPath: ['appPrivateApiName', appDefinitionId]})
        const appEditorApiNamePointer = (appDefinitionId: string) =>
            getPointer('platform', pointerType, {innerPath: ['appEditorApiName', appDefinitionId]})
        const getAppStatePointer = () => getPointer('appState', pointerType)
        const getControllerStatePointer = (controllerId: string) =>
            getPointer('appState', pointerType, {innerPath: [controllerId]})
        const getControllerStageDataPointer = (
            appDefinitionId: string,
            controllerType: string,
            controllerState: string
        ) =>
            getPointer('appManifest', pointerType, {
                innerPath: [appDefinitionId, 'controllersStageData', controllerType, controllerState]
            })
        const getControllerRolePointer = (
            appDefinitionId: string,
            controllerType: string,
            controllerState: string,
            role: string
        ) =>
            getPointer('appManifest', pointerType, {
                innerPath: [
                    appDefinitionId,
                    'controllersStageData',
                    controllerType,
                    controllerState,
                    'connections',
                    role
                ]
            })

        const getPagesPlatformApplicationsPointer = () =>
            getPointer('pagesPlatformApplications', pagesPlatformPointerType)
        const getPagesPlatformApplicationPointer = (appDefinitionId: string) =>
            getPointer('pagesPlatformApplications', pagesPlatformPointerType, {innerPath: [appDefinitionId]})

        const getSemanticAppVersionPointer = (appDefinitionId: string) =>
            getPointer('semanticAppVersions', semanticAppVersionsPointerType, {innerPath: [appDefinitionId]})
        const getSemanticAppVersionsPointer = () => getPointer('semanticAppVersions', semanticAppVersionsPointerType)

        const getAppInstallationStatePointer = (appDefinitionId: string) =>
            getPointer('appsState', appsStatePointerType, {innerPath: [appDefinitionId]})
        const getAppsInstallationStatePointer = () => getPointer('appsState', appsStatePointerType)

        return {
            platform: {
                getControllerRolePointer,
                getPlatformPointer,
                getAppManifestPointer,
                appPublicApiNamePointer,
                appPrivateApiNamePointer,
                appEditorApiNamePointer,
                getPagesPlatformApplicationPointer,
                getPagesPlatformApplicationsPointer,
                getControllerStageDataPointer,
                getControllerStatePointer,
                getAppStatePointer,
                getSemanticAppVersionPointer,
                getSemanticAppVersionsPointer,
                getAppInstallationStatePointer,
                getAppsInstallationStatePointer
            }
        }
    }

    return {
        name: 'platform',
        initializeAfterDataLoaded,
        initialize,
        createExtensionAPI,
        createPublicAPI,
        createPointersMethods,
        getDocumentDataTypes,
        initializeModels,
        createValidator,
        initialState
    }
}
export {createExtension}
